import { computed, inject, isRef, ref } from 'vue';
import { PROJECT_CONFIG_KEY } from './useProjectConfig';
const defaultCardStyles = {
    wrapper: 'group relative flex-1 min-w-0 max-w-xs basis-72 sm:basis-80 flex-shrink-0',
    card: 'flex h-full flex-col overflow-hidden rounded-3xl bg-white/95 backdrop-blur-sm border-2 border-gray-200 transition-all duration-300 hover:-translate-y-2 hover:border-brand-pink-300 hover:shadow-lg cursor-pointer',
    imageWrapper: 'relative aspect-square overflow-hidden bg-white',
    image: 'h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105',
    placeholder: 'absolute inset-0 bg-gradient-to-br from-brand-pink-500/20 to-brand-violet-500/20 flex items-center justify-center text-brand-violet-600 font-semibold text-sm tracking-wide',
    content: 'flex flex-1 flex-col gap-4 p-5',
    infoStack: 'flex flex-col gap-2',
    titleRow: 'flex items-start justify-between gap-3',
    title: 'text-lg font-bold text-gray-900 leading-tight line-clamp-2',
    price: 'text-brand-pink-600 font-semibold whitespace-nowrap',
    originalPrice: 'text-sm text-gray-400 line-through',
    description: 'text-sm text-gray-600 line-clamp-3 min-h-[3.5rem]',
    footer: 'mt-auto flex items-center justify-between',
    category: 'flex items-center gap-2 text-xs uppercase tracking-wide text-gray-400',
    button: 'inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-pink-500 to-brand-violet-500 px-4 py-2 text-white text-sm font-semibold hover:scale-[1.02] transition-transform',
    badges: {
        leftBase: 'absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold',
        rightBase: 'absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold',
        discount: 'bg-red-500 text-white shadow-lg shadow-red-500/30',
        popular: 'bg-yellow-400 text-black shadow-lg shadow-yellow-400/30',
        sale: 'bg-brand-violet-600 text-white shadow-lg shadow-brand-violet-600/30'
    }
};
const cloneDefault = () => ({
    ...defaultCardStyles,
    badges: { ...defaultCardStyles.badges }
});
const mergeCardStyles = (...layers) => {
    let result = cloneDefault();
    for (const layer of layers) {
        if (!layer) {
            continue;
        }
        result = {
            ...result,
            ...layer,
            badges: {
                ...result.badges,
                ...(layer.badges ?? {})
            }
        };
    }
    return result;
};
const resolveRef = (value) => {
    if (isRef(value)) {
        return value;
    }
    return ref(value);
};
export function useProductCardStyles(options = {}) {
    const projectConfig = inject(PROJECT_CONFIG_KEY, null);
    const registry = computed(() => projectConfig?.branding?.componentStyles?.productCard);
    const styleKeyRef = options.styleKey ? resolveRef(options.styleKey) : ref(undefined);
    const overridesRef = options.overrides ? resolveRef(options.overrides) : ref(undefined);
    const cardStyles = computed(() => {
        const stylesRegistry = registry.value;
        const explicitVariantKey = styleKeyRef.value;
        const preferredVariantKey = stylesRegistry?.defaultVariant;
        const resolvedVariantKey = explicitVariantKey ?? preferredVariantKey ?? 'default';
        const registryDefault = stylesRegistry?.default;
        const registryDefaultVariant = stylesRegistry?.variants?.default;
        const preferredVariant = !explicitVariantKey && preferredVariantKey && preferredVariantKey !== 'default'
            ? stylesRegistry?.variants?.[preferredVariantKey]
            : undefined;
        const shouldApplyResolved = resolvedVariantKey !== 'default' && (explicitVariantKey ? true : resolvedVariantKey !== preferredVariantKey);
        const resolvedVariant = shouldApplyResolved
            ? stylesRegistry?.variants?.[resolvedVariantKey]
            : undefined;
        return mergeCardStyles(registryDefault, registryDefaultVariant, preferredVariant, resolvedVariant, overridesRef.value);
    });
    return {
        cardStyles,
        defaultCardStyles
    };
}
export { defaultCardStyles };
